import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faMapPin as fasMapPin } from "@fortawesome/free-solid-svg-icons";
import Panel, { PanelBody } from "./Panel";
import Label from "./Label";
import RadioButton from "./RadioButton";
import FleetContainer from "../containers/FleetContainer";
import ExportCsvContainer from "../containers/ExportCsvContainer";

library.add(fasMapPin);

const InlineBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NoData = styled.p`
  text-align: center;
  color: #b8c4c9;
  font-size: 1.143em;
  font-family: Lato;
  font-weight: 300;
`;

const FleetView = styled.div`
  position: relative;
  margin-top: 10px;
`;

const Counter = styled.p`
  font-size: 12px;
  margin: 0 0 5px 10px;
  color: #56666c;
`;

const CheckBox = styled.input`
  cursor: pointer;
  position: absolute;
  top: 0.571em;
`;

const Filter = ({
  fleetIds,
  fleetsCount: propFleetsCount,
  visitsCount: propVisitsCount,
  selectedFleetIds,
  updateFilterState,
  showSideBar,
}) => {
  const [selectedFleets, setSelectedFleets] = useState([]);
  const [fleetsCount, setFleetsCount] = useState(0);
  const [visitsCount, setVisitsCount] = useState(0);

  useEffect(() => {
    setSelectedFleets(fleetIds);
    setFleetsCount(propFleetsCount);
    setVisitsCount(propVisitsCount);
  }, []);

  useEffect(() => {
    if (
      JSON.stringify(fleetIds) !== JSON.stringify(selectedFleets) ||
      propVisitsCount !== visitsCount
    ) {
      setSelectedFleets(fleetIds);
      setFleetsCount(propFleetsCount);
      setVisitsCount(propVisitsCount);
    }
  }, [fleetIds, propFleetsCount, propVisitsCount]);

  useEffect(() => {
    if (JSON.stringify(selectedFleetIds) !== JSON.stringify(selectedFleets)) {
      setSelectedFleets(selectedFleetIds);
    }
  }, [selectedFleetIds]);

  const onCheckBoxCheck = (fleetId) => (e) => {
    let newSelectedFleets = [];
    if (e.target.checked) {
      newSelectedFleets = [...selectedFleets, fleetId];
    } else {
      newSelectedFleets = selectedFleets.filter((id) => id !== fleetId);
    }
    setSelectedFleets(newSelectedFleets);
    updateFilterState({ fleetIds: newSelectedFleets });
  };

  const onAllChange = (e) => {
    const isAll = e.target.value === "1";
    const newSelectedFleets = isAll ? fleetIds : [];
    setSelectedFleets(newSelectedFleets);
    updateFilterState({ fleetIds: newSelectedFleets });
  };

  return (
    <Panel
      margin="1.429em 0 0 0"
      style={{ display: showSideBar ? "block" : "none" }}
    >
      <PanelBody
        style={{
          borderBottom: "1px solid #ECEFF1",
          borderRadius: 0,
          paddingTop: "12px",
          paddingBottom: "12px",
        }}
      >
        <InlineBlock style={{ marginBottom: 2 }}>
          <Label style={{ fontSize: "14px" }}>Fleet</Label>
          <InlineBlock>
            <Counter>
              <FontAwesomeIcon icon={"truck"} flip="horizontal" /> Drivers:{" "}
              {fleetsCount}
            </Counter>
            <Counter>
              <FontAwesomeIcon icon={"map-pin"} /> Stops: {visitsCount}
            </Counter>
          </InlineBlock>
        </InlineBlock>
        <InlineBlock>
          <RadioButton
            name="all"
            value={1}
            label="Select All"
            checked={propFleetsCount + 1 === selectedFleets.length}
            onChange={onAllChange}
          />
          <RadioButton
            name="all"
            value={0}
            label="Unselect All"
            checked={selectedFleets.length === 0}
            onChange={onAllChange}
          />
          <ExportCsvContainer disabled={!fleetIds[0]} />
        </InlineBlock>
      </PanelBody>
      {!fleetIds[0] && (
        <PanelBody>
          <NoData>No Data</NoData>
        </PanelBody>
      )}
      {fleetIds[0] && (
        <PanelBody padding="0 1em .6em">
          {fleetIds.map((fleetId) => (
            <FleetView key={fleetId}>
              <CheckBox
                type="checkbox"
                checked={selectedFleets.indexOf(fleetId) > -1}
                onChange={onCheckBoxCheck(fleetId)}
              />
              <FleetContainer fleetId={fleetId} />
            </FleetView>
          ))}
        </PanelBody>
      )}
    </Panel>
  );
};

Filter.propTypes = {
  fleetIds: PropTypes.array.isRequired,
  fleetsCount: PropTypes.number.isRequired,
  visitsCount: PropTypes.number.isRequired,
  selectedFleetIds: PropTypes.array.isRequired,
  updateFilterState: PropTypes.func.isRequired,
  showSideBar: PropTypes.bool,
};

export default Filter;
